import IconTrash from 'assets/images/icon-delete.svg'
import DropdownBase, { Option } from 'components/partials/dropDown'
import InputBase from 'components/partials/inputBase'
import { isEqual, isNumber } from 'lodash'
import {
  IAgencyGroupJobConfig,
  ISubEmailManagementConfig,
  IType,
} from 'pages/agency-group-management/interface'
import React, { useEffect, useState } from 'react'
import UploadHTMLAndCSSFile from './uploadFile'
import ButtonBase from 'components/partials/button'
import { onHandleCopyItem } from 'helpers/functionUtils'
import PreviewDialog from './previewDialog'
import { AGENCY_GROUP } from 'constants/common'

const TYPE_OPTIONS: IDropdownTypeOption[] = [
  {
    id: 1,
    key: 'DOCUMENT_URL',
    name: 'Document URL',
    type: 'DOCUMENT_URL',
  },
  {
    id: 2,
    key: 'HTML_CSS',
    name: 'HTML/CSS',
    type: 'HTML_CSS',
  },
]
interface Props {
  config: IAgencyGroupJobConfig
  subEmailConfigs: ISubEmailManagementConfig[]
  isEditMode: boolean
  setCustomConfigByIndex?: (config: IAgencyGroupJobConfig) => void
  handleRemoveOneCustom?: (index: number | null) => void
}
interface IDropdownTDayOption extends Option {
  subEmailManagementConfig: ISubEmailManagementConfig | null
}
interface IDropdownTypeOption extends Option {
  type: IType
}
interface IDefaultValue {
  tDayConfig: { key: string; value: string }
  type: string
  emailTemplateURL: string
}

const CustomTemplateForStage: React.FC<Props> = ({
  isEditMode = false,
  config,
  subEmailConfigs,
  setCustomConfigByIndex = () => {},
  handleRemoveOneCustom = () => {},
}) => {
  const [dropDownTDayOptions, setDropdownTDayOptions] = useState<IDropdownTDayOption[]>([])
  const [dropDownTypeOptions] = useState<IDropdownTypeOption[]>(TYPE_OPTIONS)
  const [defaultInputValues, setDefaultInputValues] = useState<IDefaultValue>()
  const [initValueTDayOption, setInitValueTDayOption] = useState<IDropdownTDayOption>()
  const [initValueTypeOption, setInitValueTypeOption] = useState<IDropdownTypeOption>()
  const [currentConfig, setCurrentConfig] = useState<IAgencyGroupJobConfig>(config)
  const [isOpenPreviewDialog, setIsOpenPreviewDialog] = useState(false)

  useEffect(() => {
    if (subEmailConfigs.length === 0) return

    const options: IDropdownTDayOption[] = []
    for (const subEmailConfig of subEmailConfigs) {
      const { configId: id, key: name, value } = subEmailConfig.tDayConfig
      options.push({
        id,
        name,
        description: value,
        subEmailManagementConfig: subEmailConfig,
      })
    }

    setDropdownTDayOptions(options)
  }, [subEmailConfigs])

  useEffect(() => {
    if (dropDownTDayOptions.length === 0) return

    const tDayConfig = config.subEmailManagementConfig?.tDayConfig
    if (tDayConfig) {
      const temp = dropDownTDayOptions.find((item) => item.id === tDayConfig.configId)
      if (temp) {
        setInitValueTDayOption(temp)
      } else {
        setInitValueTDayOption(undefined)
      }
    }
  }, [dropDownTDayOptions])

  useEffect(() => {
    setCurrentConfig(config)
    if (!isEditMode) {
      handleSetDefaultValueForViewMode(config)
    } else {
      handleSetDefaultValuesForEditMode(config)
    }
  }, [])


  useEffect(() => {
    if (!isEqual(config, currentConfig)) {
      setCustomConfigByIndex(currentConfig)
    }
  }, [currentConfig])

  const handleSetDefaultValueForViewMode = (config: IAgencyGroupJobConfig) => {
    const tDayConfig = config.subEmailManagementConfig?.tDayConfig ?? null
    const type = config.customEmailType

    if (!tDayConfig) return

    setDefaultInputValues({
      // tDayConfig: `${tDayConfig?.key} : ${tDayConfig?.value} days`,
      tDayConfig: { key: tDayConfig.key, value: tDayConfig.value },
      type: type?.toString() ?? '',
      emailTemplateURL: config.emailTemplateURL ?? '',
    })
  }

  const handleSetDefaultValuesForEditMode = (config: IAgencyGroupJobConfig) => {
    const { tDayConfig = null } = config.subEmailManagementConfig ?? {}
    const { customEmailType: type } = config

    const tDayOption = dropDownTDayOptions.find((item) => item.id === tDayConfig?.configId)
    setInitValueTDayOption(tDayOption ?? undefined)

    const typeOption = dropDownTypeOptions.find((item) => item.type === type)
    setInitValueTypeOption(typeOption ?? undefined)
  }

  const handleDeleteBtn = () => {
    handleRemoveOneCustom(currentConfig.index)
  }

  const onSelectTDayOption = (option: IDropdownTDayOption) => {
    const customConfigTemplate: IAgencyGroupJobConfig = {
      ...config,
      subEmailManagementConfigId:
        option.subEmailManagementConfig?.subEmailManagementConfigId ?? null,
      subEmailManagementConfig: option.subEmailManagementConfig ?? null,
    }
    setCurrentConfig(customConfigTemplate)
  }

  const onSelectTypeOption = (option: IDropdownTypeOption) => {
    if (option.type) {
      const customConfigTemplate: IAgencyGroupJobConfig = {
        ...config,
        customEmailType: option.type,
      }
      setCurrentConfig(customConfigTemplate)
    }
  }

  const onChangeEmailTemplateURL = (value: string) => {
    const customConfigTemplate: IAgencyGroupJobConfig = { ...config, emailTemplateURL: value }
    setCurrentConfig(customConfigTemplate)
  }

  const onClickCopyBtn = async () => {
    let value = ''
    if (currentConfig.customEmailType === AGENCY_GROUP.EMAIL_TEMPLATE_TYPE.DOCUMENT_URL) {
      value = currentConfig.emailTemplateURL ?? ''
    } else if (currentConfig.customEmailType === AGENCY_GROUP.EMAIL_TEMPLATE_TYPE.HTML_CSS) {
      value = currentConfig.htmlTemplateString ?? ''
    }
    await onHandleCopyItem(value, 'Copied', 'Something wrong')
  }

  const onClickPreviewBtn = () => {
    setIsOpenPreviewDialog(true)
  }

  const handleStoreHtmlTemplateString = (htmlTemplateString: string) => {
    const customConfigTemplate: IAgencyGroupJobConfig = { ...currentConfig, htmlTemplateString }
    setCurrentConfig(customConfigTemplate)
  }

  const initValueDropdownTypeOption = () => {
    let valueType = ''

    if (isEditMode && initValueTypeOption) {
      valueType = initValueTypeOption.type as string
    } else if (!isEditMode && defaultInputValues) {
      valueType = defaultInputValues.type
    }
    return valueType
  }

  if (!isNumber(currentConfig.index)) return null

  return (
    <div className="flex space-x-2 w-full" key={config.index}>
      <div className="w-[15%]">
        <DropdownBase
          className="w-full"
          options={dropDownTDayOptions}
          placeHolder="Select T-Day"
          hasDescription={true}
          handleOptions={(option) => onSelectTDayOption(option as IDropdownTDayOption)}
          initialValue={isEditMode ? initValueTDayOption?.name : defaultInputValues?.tDayConfig.key}
          initialDescription={
            isEditMode && initValueTDayOption
              ? initValueTDayOption.description
              : !isEditMode && defaultInputValues?.tDayConfig.value
              ? defaultInputValues.tDayConfig.value
              : ''
          }
          isDisabled={!isEditMode}
          buttonClassName={!isEditMode ? 'bg-gray-100' : ''}
        />
      </div>

      <div className="w-[20%]">
        <DropdownBase
          className="w-full"
          options={dropDownTypeOptions}
          placeHolder="Select Type"
          handleOptions={(option) => onSelectTypeOption(option as IDropdownTypeOption)}
          initialValue={initValueDropdownTypeOption()}
          isDisabled={!isEditMode}
          buttonClassName={!isEditMode ? 'bg-gray-100' : ''}
        />
      </div>

      <div className="flex items-center w-[65%]">
        {isEditMode ? (
          currentConfig.customEmailType === AGENCY_GROUP.EMAIL_TEMPLATE_TYPE.HTML_CSS ? (
            <UploadHTMLAndCSSFile
              // config={currentConfig}
              htmlString={currentConfig.htmlTemplateString ?? ''}
              sendingEmailSubject={currentConfig.subEmailManagementConfig?.sendingEmailSubject}
              handleStoreHtmlTemplateString={handleStoreHtmlTemplateString}
            />
          ) : (
            <InputBase
              name={`edit-document-url-${config.agencyGroupJobConfigId}`}
              className="w-full"
              defaultValue={config.emailTemplateURL ?? ''}
              handleChange={onChangeEmailTemplateURL}
            />
          )
        ) : (
          <InputBase
            name={`view-document-url-${config.agencyGroupJobConfigId}`}
            className="w-full bg-gray-100"
            value={
              config.customEmailType === AGENCY_GROUP.EMAIL_TEMPLATE_TYPE.DOCUMENT_URL
                ? defaultInputValues?.emailTemplateURL ?? ''
                : (config.htmlTemplateString as string)
            }
            disable={true}
          />
        )}
      </div>

      {isEditMode ? (
        <img src={IconTrash} alt="iconTrash" className="cursor-pointer" onClick={handleDeleteBtn} />
      ) : currentConfig.customEmailType === AGENCY_GROUP.EMAIL_TEMPLATE_TYPE.DOCUMENT_URL ? (
        <ButtonBase className="min-w-[120px]" onClick={onClickCopyBtn}>
          Copy
        </ButtonBase>
      ) : (
        <ButtonBase className="min-w-[120px]" onClick={onClickPreviewBtn}>
          Preview
        </ButtonBase>
      )}

      {isOpenPreviewDialog && currentConfig.subEmailManagementConfig?.sendingEmailSubject && (
        <PreviewDialog
          sendingEmailSubject={currentConfig.subEmailManagementConfig.sendingEmailSubject}
          index={currentConfig.index}
          // config={currentConfig}
          handleClosePreviewDialog={() => {
            setIsOpenPreviewDialog(false)
          }}
          htmlString={currentConfig.htmlTemplateString ?? ''}
          isLoadingData={false}
          isOpenPreview={isOpenPreviewDialog}
          key={`Preview-dialog-${currentConfig.agencyGroupJobConfigId}`}
        />
      )}
    </div>
  )
}

export default CustomTemplateForStage
